import type {
  AssetGraphResponseDto,
  ExternalNetworkEntityKind,
} from '@terra/shared-contracts-external-network';

type AssetGraphNode = AssetGraphResponseDto['nodes'][number];

const ROOT_ENTRY_KINDS = new Set<ExternalNetworkEntityKind>([
  'DOMAIN',
  'HOST',
  'NETBLOCK',
  'GATEWAY',
]);

const ROOT_PARENT_KINDS = new Set<ExternalNetworkEntityKind>([
  'DOMAIN',
  'HOST',
  'NETBLOCK',
  'STORAGE_SERVICE',
]);

export function isInternetExposedStorage(node: AssetGraphNode) {
  return (
    node.kind === 'STORAGE_SERVICE' &&
    (node.properties.publicAccess === 'PUBLIC' ||
      node.properties.websiteEnabled === true)
  );
}

export function getRouteRootEntryNodeIds(graph: AssetGraphResponseDto) {
  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));
  const nodesWithTowardInternetParent = new Set<string>();

  for (const edge of graph.edges) {
    const source = nodesById.get(edge.source);
    const target = nodesById.get(edge.target);

    if (!source || !target) {
      continue;
    }

    switch (edge.type) {
      case 'IS_SUBDOMAIN_OF':
      case 'RESOLVES_TO':
      case 'WITHIN_NETBLOCK':
        nodesWithTowardInternetParent.add(source.id);
        break;
      case 'FRONTED_BY':
      case 'ORIGIN_OF':
        nodesWithTowardInternetParent.add(target.id);
        break;
      case 'SERVES_VHOST':
        if (source.kind === 'HOST' || source.kind === 'GATEWAY') {
          nodesWithTowardInternetParent.add(source.id);
        } else if (target.kind === 'HOST' || target.kind === 'GATEWAY') {
          nodesWithTowardInternetParent.add(target.id);
        }

        break;
      case 'RUNS_SERVICE':
      case 'RUNS_SOFTWARE':
        if (source.kind === 'NETWORK_SERVICE' || source.kind === 'SOFTWARE') {
          nodesWithTowardInternetParent.add(source.id);
        } else {
          nodesWithTowardInternetParent.add(target.id);
        }

        break;
      default:
        break;
    }
  }

  const rootEntryNodeIds = new Set<string>();

  for (const node of graph.nodes) {
    if (nodesWithTowardInternetParent.has(node.id)) {
      continue;
    }

    if (
      node.kind === 'NETWORK_SERVICE' ||
      node.kind === 'SOFTWARE' ||
      node.kind === 'WEAKNESS'
    ) {
      continue;
    }

    if (node.kind === 'STORAGE_SERVICE' && !isInternetExposedStorage(node)) {
      continue;
    }

    rootEntryNodeIds.add(node.id);
  }

  return rootEntryNodeIds;
}

export function getRootEntryNodeIds(graph: AssetGraphResponseDto) {
  const rootEntryNodeIds = new Set<string>();
  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));
  const childIds = new Set(
    graph.edges
      .filter((edge) => {
        const sourceKind = nodesById.get(edge.source)?.kind;

        return (
          sourceKind !== undefined &&
          sourceKind !== 'WEAKNESS' &&
          ROOT_PARENT_KINDS.has(sourceKind)
        );
      })
      .map((edge) => edge.target),
  );

  for (const node of graph.nodes) {
    const isRootKind =
      node.kind !== 'WEAKNESS' &&
      (ROOT_ENTRY_KINDS.has(node.kind) || isInternetExposedStorage(node));

    if (isRootKind && !childIds.has(node.id)) {
      rootEntryNodeIds.add(node.id);
    }
  }

  return rootEntryNodeIds;
}
